import { supabase } from './supabaseClient';
import { CardRefundLeg, finishCardRefundLegs } from './refunds';

export interface ProductSale {
  id: string;
  business_id: string;
  customer_id?: string;
  invoice_id?: string | null;
  sale_number: string;
  subtotal_amount: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  discount_amount?: number;
  discount_details?: Array<{
    id: string;
    title: string;
    type: 'percentage' | 'fixed';
    amount: number;
  }>;
  original_subtotal?: number;
  payment_status: 'paid' | 'unpaid' | 'partial';
  payment_method?: string;
  payment_date?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface ProductSaleItem {
  id: string;
  sale_id: string;
  stock_item_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  tax_exempt: boolean;
  tax_rate: number;
  tax_amount: number;
  total_price: number;
  created_at: string;
}

export interface ProductSaleWithDetails extends ProductSale {
  customer_first_name?: string;
  customer_last_name?: string;
  customer_email?: string;
  customer_phone?: string;
  created_by_email?: string;
  item_count: number;
  total_items_sold: number;
  items?: ProductSaleItem[];
}

export interface CreateProductSaleData {
  business_id: string;
  customer_id?: string;
  subtotal_amount: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  discount_amount?: number;
  discount_details?: Array<{
    id: string;
    title: string;
    type: 'percentage' | 'fixed';
    amount: number;
  }>;
  original_subtotal?: number;
  payment_status?: 'paid' | 'unpaid' | 'partial';
  payment_method?: string;
  payment_date?: string;
  /** Payment amount in dollars; defaults to the full total (paid sales only). */
  payment_amount?: number;
  payment_reference?: string;
  gift_card_id?: string;
  /** Client-side total in cents; the server aborts if its total differs. */
  expected_total_cents?: number;
  notes?: string;
  items: {
    stock_item_id: string;
    product_name: string;
    quantity: number;
    unit_price: number;
    tax_exempt: boolean;
    tax_rate: number;
    tax_amount: number;
    total_price: number;
  }[];
}

export interface CounterSaleQuote {
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  combinedRatePercent: number;
  taxBreakdown: Array<{ name: string; ratePercent: number; amount: number }>;
}

export interface CounterSaleDiscountInput {
  type: 'percentage' | 'fixed';
  /** Percentage (e.g. 10 for 10%) or dollar amount for fixed. */
  value: number;
}

/** Convert the carts' applied-discount shape into the quote/RPC input. */
export function toCounterSaleDiscount(
  applied: { type: 'percentage' | 'fixed'; value: number; discountAmount: number } | null | undefined
): CounterSaleDiscountInput | null {
  if (!applied) return null;
  return applied.type === 'percentage'
    ? { type: 'percentage', value: applied.value }
    : { type: 'fixed', value: applied.discountAmount };
}

/**
 * Server-side quote using the real invoice engine math (business tax rates,
 * per-product tax exemption, largest-remainder discount allocation). This is
 * the number the invoice will show — use it for all cart totals.
 */
export async function quoteCounterSale(
  businessId: string,
  items: Array<{
    stock_item_id: string | null;
    quantity: number;
    unit_price: number;
    /** Fee/deposit lines have no stock item; deposits pass taxable: false. */
    taxable?: boolean;
  }>,
  discount?: CounterSaleDiscountInput | null
): Promise<CounterSaleQuote> {
  const { data, error } = await supabase.rpc('quote_counter_sale', {
    p_business_id: businessId,
    p_items: items,
    p_discount: discount ?? null,
  });
  if (error) throw error;
  return {
    subtotal: (data?.subtotal_cents ?? 0) / 100,
    discount: (data?.discount_cents ?? 0) / 100,
    tax: (data?.tax_cents ?? 0) / 100,
    total: (data?.total_cents ?? 0) / 100,
    combinedRatePercent: (data?.combined_rate_bps ?? 0) / 100,
    taxBreakdown: (data?.tax_breakdown ?? []).map((b: any) => ({
      name: b.name,
      ratePercent: (b.rate_bps ?? 0) / 100,
      amount: (b.amount_cents ?? 0) / 100,
    })),
  };
}

// ── Point of sale checkout (shared fulfillment engine) ───────────────────────
// A counter sale settles through the same server engine as an order delivery
// (settle_fulfillment): deposits rebuilt from actual returns, container
// ledger, wallet-first, rentals activated — with no order row.

export interface CounterCheckoutLine {
  stock_item_id: string;
  product_name: string;
  quantity: number;
  /** Sale price, or the per-period rate for rental lines. */
  unit_price: number;
  /** 'rental' hands equipment over on a recurring agreement. */
  line_type?: 'product' | 'rental';
}

export interface CounterReturnInput {
  stock_item_id: string;
  returned: number;
  lost?: number;
}

export type CounterRefundMode = 'wallet' | 'invoice' | 'cash' | 'e_transfer' | 'card';

/** Server-computed deposit math for one deposit-bearing product in the cart. */
export interface CounterDepositSummary {
  stock_item_id: string;
  product_name: string;
  deposit_cents: number;
  delivered: number;
  returned: number;
  lost: number;
  net_new: number;
  excess_refund_cents: number;
  prior_balance: number;
  /** Returned jugs the record couldn't account for — put on record so the count self-corrects. */
  untracked: number;
  /** Returned jugs refunded at today's rate because no deposit was on record for them. */
  unmatched?: number;
  unmatched_rate_cents?: number;
}

export interface CounterCheckoutQuote extends CounterSaleQuote {
  deposits: CounterDepositSummary[];
  /** Excess-deposit refund taken off this invoice (refund mode 'invoice'). */
  invoiceCreditCents: number;
  /** Total excess-deposit refund owed to the customer, before routing. */
  excessRefundCents: number;
}

export interface CounterCheckoutPayment {
  method: string;
  /** Dollars; defaults to the full invoice total. */
  amount?: number;
  reference?: string;
  gift_card_id?: string;
}

export interface CounterCheckoutResult {
  sale_id: string;
  sale_number: string;
  invoice_id: string;
  invoice_number: string;
  invoice_status: string;
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  total_cents: number;
  payment_id: string | null;
  deposits: CounterDepositSummary[];
  wallet_credit_cents: number;
  invoice_credit_cents: number;
  external_refund_cents: number;
  /** Excess deposit refund that went back to a card (after Stripe ran). */
  card_refund_cents: number;
  refund_group_id: string | null;
  card_refund_legs: CardRefundLeg[];
  excess_refund_mode: CounterRefundMode;
  wallet_applied_cents: number;
  rental_agreement_ids: string[];
  rentals_activated: number;
}

function parseCheckoutQuote(data: any): CounterCheckoutQuote {
  return {
    subtotal: (data?.subtotal_cents ?? 0) / 100,
    discount: (data?.discount_cents ?? 0) / 100,
    tax: (data?.tax_cents ?? 0) / 100,
    total: (data?.total_cents ?? 0) / 100,
    combinedRatePercent: (data?.combined_rate_bps ?? 0) / 100,
    taxBreakdown: (data?.tax_breakdown ?? []).map((b: any) => ({
      name: b.name,
      ratePercent: (b.rate_bps ?? 0) / 100,
      amount: (b.amount_cents ?? 0) / 100,
    })),
    deposits: (data?.deposits ?? []) as CounterDepositSummary[],
    invoiceCreditCents: Number(data?.invoice_credit_cents ?? 0),
    excessRefundCents: Number(data?.excess_refund_cents ?? 0),
  };
}

/** Preview exactly what checkoutCounterSale will bill for this cart. */
export async function quoteCounterCheckout(args: {
  businessId: string;
  customerId?: string | null;
  items: CounterCheckoutLine[];
  discount?: CounterSaleDiscountInput | null;
  returns?: CounterReturnInput[] | null;
  refundMode?: CounterRefundMode;
}): Promise<CounterCheckoutQuote> {
  const { data, error } = await supabase.rpc('quote_counter_checkout', {
    p_business_id: args.businessId,
    p_customer_id: args.customerId ?? null,
    p_items: args.items,
    p_discount: args.discount ?? null,
    p_returns: args.returns ?? null,
    p_excess_refund_mode: args.refundMode ?? 'wallet',
  });
  if (error) throw error;
  return parseCheckoutQuote(data);
}

/** Point-of-sale checkout: one atomic server call, no order row. */
export async function checkoutCounterSale(args: {
  businessId: string;
  customerId?: string | null;
  items: CounterCheckoutLine[];
  discount?: CounterSaleDiscountInput | null;
  returns?: CounterReturnInput[] | null;
  /** null = leave the invoice open (send / pay link). */
  payment?: CounterCheckoutPayment | null;
  /** Quoted total in cents; the server aborts if its total differs. */
  expectedTotalCents?: number | null;
  refundMode?: CounterRefundMode;
  notes?: string | null;
}): Promise<CounterCheckoutResult> {
  const { data, error } = await supabase.rpc('checkout_counter_sale', {
    p_business_id: args.businessId,
    p_items: args.items,
    p_customer_id: args.customerId ?? null,
    p_discount: args.discount ?? null,
    p_returns: args.returns ?? null,
    p_payment: args.payment ?? null,
    p_expected_total_cents: args.expectedTotalCents ?? null,
    p_excess_refund_mode: args.refundMode ?? 'wallet',
    p_notes: args.notes ?? null,
  });
  if (error) throw error;
  const result: CounterCheckoutResult = {
    ...(data as CounterCheckoutResult),
    payment_id: data?.payment_id ?? null,
    deposits: (data?.deposits ?? []) as CounterDepositSummary[],
    rental_agreement_ids: (data?.rental_agreement_ids ?? []) as string[],
    card_refund_legs: (data?.card_refund_legs ?? []) as CardRefundLeg[],
    refund_group_id: data?.refund_group_id ?? null,
    card_refund_cents: Number(data?.card_refund_cents ?? 0),
  };
  // Excess-deposit card legs were written pending inside the sale — run them.
  const processed = await finishCardRefundLegs(result);
  if (processed) {
    result.card_refund_cents = processed.card_refunded_cents;
    result.wallet_credit_cents = (result.wallet_credit_cents ?? 0) + processed.wallet_credited_cents;
  }
  return result;
}

// Calculate tax for product sales
export function calculateProductSaleTax(items: { price: number; quantity: number; tax_exempt?: boolean }[], taxRate: number = 5) {
  let subtotal = 0;
  let totalTax = 0;
  
  const itemsWithTax = items.map(item => {
    const itemSubtotal = item.price * item.quantity;
    const isTaxExempt = item.tax_exempt || false;
    const itemTaxRate = isTaxExempt ? 0 : taxRate;
    const itemTaxAmount = isTaxExempt ? 0 : Math.round((itemSubtotal * itemTaxRate / 100) * 100) / 100;
    const itemTotal = itemSubtotal + itemTaxAmount;
    
    subtotal += itemSubtotal;
    totalTax += itemTaxAmount;
    
    return {
      ...item,
      tax_exempt: isTaxExempt,
      tax_rate: itemTaxRate,
      tax_amount: itemTaxAmount,
      total_price: itemTotal
    };
  });
  
  const total = subtotal + totalTax;
  
  return {
    subtotal,
    tax_rate: taxRate,
    tax_amount: totalTax,
    total: total,
    items: itemsWithTax
  };
}

// Generate sale number
export async function generateSaleNumber(businessId: string): Promise<string> {
  const { data, error } = await supabase.rpc('generate_sale_number', {
    business_uuid: businessId
  });

  if (error) throw error;
  return data;
}

// Derive the invoice-level discount from the legacy call-site shape.
function deriveDiscountInput(saleData: CreateProductSaleData): CounterSaleDiscountInput | null {
  const details = saleData.discount_details || [];
  if (details.length === 1 && details[0].type === 'percentage') {
    return { type: 'percentage', value: details[0].amount };
  }
  if ((saleData.discount_amount || 0) > 0) {
    return { type: 'fixed', value: saleData.discount_amount! };
  }
  return null;
}

// Create a new counter sale. One atomic server call creates the legacy
// product_sales rows, moves stock through the ledger, issues a real standalone
// invoice (business tax rates, per-product exemption), and records the payment
// when payment_status is 'paid'. Tax fields passed in saleData are ignored —
// the server computes them.
export async function createProductSale(saleData: CreateProductSaleData): Promise<ProductSale> {
  const payment =
    saleData.payment_status === 'paid'
      ? {
          method: saleData.payment_method || 'cash',
          ...(saleData.payment_amount != null ? { amount: saleData.payment_amount } : {}),
          ...(saleData.payment_reference ? { reference: saleData.payment_reference } : {}),
          ...(saleData.gift_card_id ? { gift_card_id: saleData.gift_card_id } : {}),
        }
      : null;

  const { data, error } = await supabase.rpc('create_counter_sale', {
    p_business_id: saleData.business_id,
    p_items: saleData.items.map(item => ({
      stock_item_id: item.stock_item_id,
      product_name: item.product_name,
      quantity: item.quantity,
      unit_price: item.unit_price,
    })),
    p_customer_id: saleData.customer_id ?? null,
    p_discount: deriveDiscountInput(saleData),
    p_discount_details: saleData.discount_details ?? null,
    p_notes: saleData.notes ?? null,
    p_payment: payment,
    p_expected_total_cents: saleData.expected_total_cents ?? null,
  });

  if (error) {
    console.error('Error creating counter sale:', error);
    throw error;
  }

  // Return the persisted row so callers (PDF generation, success modals) see
  // the server-computed totals.
  const { data: sale, error: fetchError } = await supabase
    .from('product_sales')
    .select('*')
    .eq('id', data.sale_id)
    .single();

  if (fetchError) throw fetchError;
  return sale;
}

// Get product sales for a business
export async function getProductSales(businessId: string): Promise<ProductSaleWithDetails[]> {
  const { data, error } = await supabase
    .from('product_sales_with_details')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Get a single product sale with items
export async function getProductSaleById(saleId: string): Promise<ProductSaleWithDetails | null> {
  // Get the main sale record
  const { data: sale, error: saleError } = await supabase
    .from('product_sales_with_details')
    .select('*')
    .eq('id', saleId)
    .single();

  if (saleError) throw saleError;

  // Get the sale items
  const { data: items, error: itemsError } = await supabase
    .from('product_sale_items')
    .select('*')
    .eq('sale_id', saleId)
    .order('created_at', { ascending: true });

  if (itemsError) throw itemsError;

  return {
    ...sale,
    items: items || []
  };
}

// Update payment status
export async function updatePaymentStatus(
  saleId: string,
  paymentStatus: 'paid' | 'unpaid' | 'partial',
  paymentMethod?: string,
  paymentDate?: string
): Promise<void> {
  const updateData: any = {
    payment_status: paymentStatus
  };

  if (paymentMethod) updateData.payment_method = paymentMethod;
  if (paymentDate) updateData.payment_date = paymentDate;

  const { error } = await supabase
    .from('product_sales')
    .update(updateData)
    .eq('id', saleId);

  if (error) throw error;
  // Note: stock was already deducted when the sale was created — marking a
  // sale paid must never move stock again.
}

// Delete a product sale
export async function deleteProductSale(saleId: string): Promise<void> {
  const { error } = await supabase
    .from('product_sales')
    .delete()
    .eq('id', saleId);

  if (error) throw error;
}
